import {
  ADMIN_MODULE_KEYS,
  AdminModuleKey,
  AdminRolePermissions,
} from "../../Common/models/adminRole";
import {
  ADMIN_HOME_MODULE_ROUTES,
  isEnvGateEnabled,
  pathMatchesAdminPrefix,
} from "../constants/adminHomeRoutes";
import * as routes from "../constants/routes";

export const buildEmptyAdminPermissions = (): AdminRolePermissions =>
  ADMIN_MODULE_KEYS.reduce((acc, moduleKey) => {
    acc[moduleKey] = { view: false, edit: false };
    return acc;
  }, {} as AdminRolePermissions);

export interface AdminModuleRouteConfig {
  module: AdminModuleKey;
  routes: string[];
}

/** Ordered list used for default redirect when landing on /home */
export const ADMIN_MODULE_ROUTE_ORDER: AdminModuleRouteConfig[] =
  ADMIN_HOME_MODULE_ROUTES.map(({ module, matchPaths }) => ({
    module,
    routes: matchPaths,
  }));

const pathMatchesRoute = (pathname: string, route: string): boolean =>
  pathname === route || pathname.startsWith(`${route}/`);

export const getModuleForPath = (pathname: string): AdminModuleKey | null => {
  for (const { module, matchPaths, pathPrefixes } of ADMIN_HOME_MODULE_ROUTES) {
    if (pathPrefixes?.some((prefix) => pathMatchesAdminPrefix(pathname, prefix))) {
      return module;
    }

    if (matchPaths.some((route) => pathMatchesRoute(pathname, route))) {
      return module;
    }
  }

  if (pathname === routes.ADMIN_ROLES) {
    return null;
  }

  return null;
};

export const getFirstAccessibleRoute = (
  hasPermission: (module: AdminModuleKey, action: "view" | "edit") => boolean,
  options: {
    shopEnabled: boolean;
    testEnabled: boolean;
    qaToolsEnabled: boolean;
  }
): string => {
  for (const { module, matchPaths, enabledBy } of ADMIN_HOME_MODULE_ROUTES) {
    if (!isEnvGateEnabled(enabledBy, options)) {
      continue;
    }
    if (hasPermission(module, "view")) {
      return matchPaths[0];
    }
  }

  return routes.ADMIN_NO_ACCESS;
};

export const canViewModule = (
  module: AdminModuleKey,
  hasPermission: (module: AdminModuleKey, action: "view" | "edit") => boolean,
  options: {
    shopEnabled: boolean;
    testEnabled: boolean;
    qaToolsEnabled: boolean;
  }
): boolean => {
  const routeConfig = ADMIN_HOME_MODULE_ROUTES.find(
    (route) => route.module === module
  );

  if (routeConfig && !isEnvGateEnabled(routeConfig.enabledBy, options)) {
    return false;
  }

  return hasPermission(module, "view");
};

/** Route registration gate — preserves deep links while permissions hydrate on refresh */
export const canViewModuleRoute = (
  module: AdminModuleKey,
  hasPermission: (module: AdminModuleKey, action: "view" | "edit") => boolean,
  options: {
    shopEnabled: boolean;
    testEnabled: boolean;
    qaToolsEnabled: boolean;
  },
  context: {
    pathname: string;
    isSuperAdmin: boolean;
    rolePermissions: AdminRolePermissions | null;
    hasAdminRoleCode: boolean;
  }
): boolean => {
  if (context.isSuperAdmin) {
    return true;
  }

  if (context.rolePermissions === null && context.hasAdminRoleCode) {
    return getModuleForPath(context.pathname) === module;
  }

  return canViewModule(module, hasPermission, options);
};
